import { useState, type KeyboardEvent } from "react";

import { Plus } from "lucide-react";

export interface CaptureProps {
  /**
   * Relay the capture (praxis-intent-relay §3.7). Resolves on
   * acceptance (the draft clears); REJECTS on refusal or transport
   * throw (the draft survives so the user can retry or edit).
   */
  onCapture: (text: string) => Promise<void>;
  /** A capture relay is in flight — input + submit disabled. */
  pending?: boolean;
  /** Inline error copy from the failed relay row / refused mutation. */
  error?: string | null;
}

/**
 * The live capture row — replaces the read-only `CaptureStub`. A
 * controlled draft submitted on Enter or the button; the agent applies
 * the worth-being-a-bead filter pod-side, so this component sends raw
 * text and renders honestly pending state only (ghost rows are the
 * list's concern, keyed by RelayId).
 */
export function Capture({ onCapture, pending, error }: CaptureProps) {
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const busy = pending === true || submitting;
  const canSubmit = !busy && draft.trim().length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onCapture(draft.trim());
      setDraft("");
    } catch {
      // Draft survives failure — the error copy renders below.
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <div className="mt-5">
      <div
        className={`flex items-center gap-2 rounded-2xl border border-home-ink/10 dark:border-white/10 bg-white dark:bg-surface-dark-card px-4 py-3 shadow-[0_10px_28px_-22px_rgba(33,31,28,0.5)] ${
          busy ? "opacity-70" : ""
        }`}
      >
        <Plus
          className="h-4 w-4 shrink-0 text-home-ink/40 dark:text-white/40"
          strokeWidth={2.6}
        />
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy}
          placeholder="What do you need done?"
          aria-label="Capture a task"
          className="min-w-0 flex-1 bg-transparent font-body text-[14px] text-home-ink dark:text-white placeholder:text-home-ink/40 dark:placeholder:text-white/40 outline-none disabled:cursor-wait"
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!canSubmit}
          className="shrink-0 rounded-full bg-home-ink dark:bg-white px-3.5 py-1.5 font-body text-[12px] font-semibold text-white dark:text-home-ink transition-opacity disabled:opacity-30"
        >
          {busy ? "Capturing…" : "Capture"}
        </button>
      </div>
      {error != null ? (
        <p className="mt-2 pl-1 font-body text-[12px] text-home-terracotta">
          {error}
        </p>
      ) : null}
    </div>
  );
}
